import { ydocSafeCloneNodeData } from '../../../../utils/ydocNodeData'
import type {
  EditSpecDict,
  EditSpecDictYMap,
  EditSpecOverlayClipDict,
  EditSpecSourceRefDict,
} from '../../../../generated/nodeDataTypes'
import {
  ANCHORS,
  CAPTION_STYLE_PRESETS,
  COLOR_LUTS,
  EFFECT_PRESETS,
  TEXT_ANIMATIONS,
  TEXT_STYLE_PRESETS,
  TRANSITION_DIRECTIONS,
  TRANSITION_TYPES,
  STAGE_BG_KINDS,
  STAGE_TEMPLATES,
  editSpecFingerprint,
  type EditSpecInput,
} from 'vibe-remotion/editspec'

type ResolvedSources = Readonly<Record<string, string>>

const EFFECT_NAMES: Readonly<Record<string, string>> = {
  ken_burns_in: 'kenBurnsIn',
  ken_burns_out: 'kenBurnsOut',
  punch_in: 'punchIn',
  grain: 'grain',
}

const TRANSITION_NAMES: Readonly<Record<string, string>> = {
  clock_wipe: 'clockWipe',
}

const DIRECTION_NAMES: Readonly<Record<string, string>> = {
  from_left: 'from-left',
  from_right: 'from-right',
  from_top: 'from-top',
  from_bottom: 'from-bottom',
}

const ANCHOR_MAP: Readonly<Record<string, string>> = {
  top_left: 'top-left',
  top_center: 'top-center',
  top_right: 'top-right',
  center: 'center',
  bottom_left: 'bottom-left',
  bottom_center: 'bottom-center',
  bottom_right: 'bottom-right',
}

const STAGE_TEMPLATE_MAP: Readonly<Record<string, string>> = {
  presenter_pip_tr: 'presenter-pip-tr',
  presenter_left: 'presenter-left',
  split_2up: 'split-2up',
  grid_4up: 'grid-4up',
}

function requireEnum<const T extends readonly string[]>(
  value: string,
  allowed: T,
  field: string,
): T[number] {
  const match = allowed.find((candidate) => candidate === value)
  if (match !== undefined) return match
  throw new Error(`${field} has unsupported value ${JSON.stringify(value)}`)
}

function sourceKey(source: EditSpecSourceRefDict): string {
  const base = `${source.node_id}.${source.output_port}`
  return source.history_index === undefined ? base : `${base}@${source.history_index}`
}

function resolveSource(source: EditSpecSourceRefDict, sources: ResolvedSources): string {
  const key = sourceKey(source)
  const path = sources[key]
  if (!path) throw new Error(`DreamVibe preview source ${key} is not resolved`)
  return path
}

function graphicComponentProps(
  overlay: EditSpecOverlayClipDict,
  sources: ResolvedSources,
): NonNullable<EditSpecInput['overlayClips']>[number]['componentProps'] {
  const props = { ...(overlay.component_props ?? {}) }
  const graphicSources = overlay.graphic_sources ?? []
  if (overlay.component_kind === 'image_card') {
    props.imageUrl = resolveSource(graphicSources[0], sources)
  } else if (
    overlay.component_kind === 'top_list' &&
    Array.isArray(props.items) &&
    props.items.every(
      (item): item is Record<string, string | number | boolean | null> =>
        item !== null && typeof item === 'object' && !Array.isArray(item),
    )
  ) {
    props.items = props.items.map((rawItem) => {
      const { source_index: sourceIndex, ...item } = rawItem
      return typeof sourceIndex === 'number'
        ? { ...item, thumbUrl: resolveSource(graphicSources[sourceIndex], sources) }
        : item
    })
  }
  return props
}

export function toDreamVibeInput(
  spec: EditSpecDict,
  resolvedSources: ResolvedSources,
): EditSpecInput {
  const keepOriginalAudio = spec.audio?.keep_original_audio ?? true
  return {
    version: 1,
    format: spec.format,
    clips: spec.clips.map((clip) => {
      const transitionType = clip.transition_in?.type ?? 'none'
      return {
      id: clip.id,
      sourceKind: 'video',
      sourcePath: resolveSource(clip.source, resolvedSources),
      inSeconds: clip.in_seconds,
      outSeconds: clip.out_seconds,
      speed: clip.speed ?? 1,
      keepAudio: keepOriginalAudio && !(clip.muted ?? false),
      volume: clip.volume ?? 1,
      effects: (clip.effects ?? []).map((effect) =>
        requireEnum(EFFECT_NAMES[effect] ?? effect, EFFECT_PRESETS, `clip ${clip.id} effect`),
      ),
      transform: clip.transform?.reframe === 'smart'
        ? {
            reframe: 'smart',
            focus: [{
              atSeconds: 0,
              x: clip.transform.focus_x!,
              y: clip.transform.focus_y!,
              scale: clip.transform.scale ?? 1,
            }],
          }
        : { reframe: clip.transform?.reframe ?? 'cover' },
      transitionIn: clip.transition_in
        ? {
            type: requireEnum(
              TRANSITION_NAMES[transitionType] ?? transitionType,
              TRANSITION_TYPES,
              `clip ${clip.id} transition`,
            ),
            durationInSeconds: clip.transition_in.duration_s ?? 0.4,
            direction: clip.transition_in.direction
              ? requireEnum(
                  DIRECTION_NAMES[clip.transition_in.direction] ?? clip.transition_in.direction,
                  TRANSITION_DIRECTIONS,
                  `clip ${clip.id} transition direction`,
                )
              : undefined,
          }
        : undefined,
      colorGrade: spec.color_grade
        ? {
            lut: requireEnum(spec.color_grade.lut ?? 'none', COLOR_LUTS, 'color grade LUT'),
            exposure: spec.color_grade.exposure ?? 0,
            contrast: spec.color_grade.contrast ?? 0,
            saturation: spec.color_grade.saturation ?? 0,
            temperature: spec.color_grade.temperature ?? 0,
            intensity: 1,
          }
        : undefined,
      }
    }),
    overlays: toTextOverlayInput(spec.overlays ?? []),
    overlayClips: (spec.overlay_clips ?? []).map((overlay) => ({
      id: overlay.id,
      role: overlay.role,
      layer: overlay.layer,
      sourceKind: overlay.role === 'graphic' ? 'component' : 'video',
      sourcePath: overlay.source ? resolveSource(overlay.source, resolvedSources) : undefined,
      componentKind: overlay.component_kind,
      componentProps: overlay.role === 'graphic'
        ? graphicComponentProps(overlay, resolvedSources)
        : undefined,
      theme: overlay.theme,
      startSeconds: overlay.start_seconds,
      durationSeconds: overlay.duration_seconds,
      position: overlay.x === undefined && overlay.y === undefined
        ? undefined
        : {
            x: overlay.x ?? 50,
            y: overlay.y ?? 50,
            anchor: requireEnum(
              ANCHOR_MAP[overlay.anchor ?? 'center'] ?? 'center',
              ANCHORS,
              `overlay clip ${overlay.id} anchor`,
            ),
          },
      size: overlay.width_pct === undefined
        ? undefined
        : { w: overlay.width_pct, h: overlay.height_pct! },
      opacity: overlay.opacity ?? 1,
    })),
    stage: spec.stage
      ? {
          template: requireEnum(
            STAGE_TEMPLATE_MAP[spec.stage.template] ?? spec.stage.template,
            STAGE_TEMPLATES,
            'stage template',
          ),
          theme: spec.stage.theme,
          background: spec.stage.background
            ? {
                kind: requireEnum(
                  (spec.stage.background.kind ?? 'gradient') === 'blur_broll'
                    ? 'blur-broll'
                    : spec.stage.background.kind ?? 'gradient',
                  STAGE_BG_KINDS,
                  'stage background kind',
                ),
                color: spec.stage.background.color,
              }
            : undefined,
        }
      : undefined,
    captions: spec.captions
      ? {
          stylePreset: requireEnum(
            spec.captions.style_preset,
            CAPTION_STYLE_PRESETS,
            'caption style',
          ),
          fontScale: spec.captions.font_scale ?? 1,
          segments: spec.captions.segments.map((segment) => ({
            id: segment.id, style: segment.style, visible: segment.visible,
            width_pct: segment.width_pct, rotation: segment.rotation, layer: segment.layer,
            x: segment.x, y: segment.y, anchor: segment.anchor,
            text: segment.text,
            startSeconds: segment.start_seconds,
            endSeconds: segment.end_seconds,
            words: segment.words && segment.words.length > 0
              ? segment.words.map((word) => ({
                  text: word.text,
                  startSeconds: word.start_seconds,
                  endSeconds: word.end_seconds,
                }))
              : undefined,
          })),
        }
      : undefined,
    audio: spec.audio
      ? {
          keepOriginalAudio: spec.audio.keep_original_audio ?? true,
          originalAudioGain: spec.audio.original_audio_gain ?? 1,
          music: spec.audio.tracks?.find((track) => track.role === 'music')
            ? (() => {
                const track = spec.audio?.tracks?.find((item) => item.role === 'music')
                if (!track) return undefined
                return {
                  sourcePath: resolveSource(track.source, resolvedSources),
                  gain: track.gain ?? 1,
                  startSeconds: track.start_seconds,
                  durationSeconds:
                    track.end_seconds === undefined
                      ? undefined
                      : track.end_seconds - track.start_seconds,
                  loop: track.loop ?? true,
                  fadeInSeconds: track.fade_in_seconds ?? 0,
                  fadeOutSeconds: track.fade_out_seconds ?? 0,
                  duckGain: spec.audio?.duck_gain ?? 0.35,
                }
              })()
            : undefined,
          voiceover: (spec.audio.tracks ?? [])
            .filter((track) => track.role === 'voiceover')
            .map((track) => ({
              sourcePath: resolveSource(track.source, resolvedSources),
              startSeconds: track.start_seconds,
              durationSeconds:
                track.end_seconds === undefined
                  ? undefined
                  : track.end_seconds - track.start_seconds,
              gain: track.gain ?? 1,
              fadeInSeconds: track.fade_in_seconds ?? 0,
              fadeOutSeconds: track.fade_out_seconds ?? 0,
            })),
          sfx: (spec.audio.tracks ?? [])
            .filter((track) => track.role === 'sfx')
            .map((track) => ({
              sourcePath: resolveSource(track.source, resolvedSources),
              startSeconds: track.start_seconds,
              durationSeconds:
                track.end_seconds === undefined
                  ? undefined
                  : track.end_seconds - track.start_seconds,
              gain: track.gain ?? 1,
              fadeInSeconds: track.fade_in_seconds ?? 0,
              fadeOutSeconds: track.fade_out_seconds ?? 0,
            })),
        }
      : undefined,
  }
}

export async function fingerprintDreamwriteEditSpec(spec: EditSpecDict | EditSpecDictYMap): Promise<string> {
  return editSpecFingerprint(ydocSafeCloneNodeData(spec))
}

export async function fingerprintDreamwriteRender(
  spec: EditSpecDict | EditSpecDictYMap,
  sourceRevisions: ResolvedSources,
): Promise<string> {
  return editSpecFingerprint({ edit_spec: ydocSafeCloneNodeData(spec), sources: sourceRevisions })
}

export function dreamwriteEditSpecDurationSeconds(spec: EditSpecDict): number {
  const fps = spec.format.fps
  const clipFrames = spec.clips.map((clip) =>
    Math.max(1, Math.round(((clip.out_seconds - clip.in_seconds) / (clip.speed ?? 1)) * fps)),
  )
  const transitionFrames = spec.clips.slice(1).map((clip) =>
    clip.transition_in && clip.transition_in.type !== 'none'
      ? Math.round((clip.transition_in.duration_s ?? 0.4) * fps)
      : 0,
  )
  return Math.max(
    1,
    ...(spec.overlays ?? []).map((item) => Math.round((item.start_seconds + item.duration_seconds) * fps)),
    ...(spec.captions?.segments ?? []).map((item) => Math.round(item.end_seconds * fps)),
    clipFrames.reduce((total, frames) => total + frames, 0) -
      transitionFrames.reduce((total, frames) => total + frames, 0),
  ) / fps
}

/** Shared Text mapping for canonical and legacy timelines. */
export function toTextOverlayInput(overlays: NonNullable<EditSpecDict['overlays']>): NonNullable<EditSpecInput['overlays']> {
  return overlays.map((overlay) => ({
      id: overlay.id,
      kind: 'text',
      style: overlay.style, visible: overlay.visible, width_pct: overlay.width_pct, rotation: overlay.rotation, layer: overlay.layer,
      text: overlay.text,
      stylePreset: requireEnum(
        overlay.style_preset ?? 'bold_caption',
        TEXT_STYLE_PRESETS,
        `overlay ${overlay.id} style`,
      ),
      animation: requireEnum(
        overlay.animation === 'slide_up' ? 'slideUp' : overlay.animation ?? 'none',
        TEXT_ANIMATIONS,
        `overlay ${overlay.id} animation`,
      ),
      position: {
        x: overlay.x,
        y: overlay.y,
        anchor: requireEnum(
          ANCHOR_MAP[overlay.anchor ?? 'center'] ?? 'center',
          ANCHORS,
          `overlay ${overlay.id} anchor`,
        ),
      },
      startSeconds: overlay.start_seconds,
      durationSeconds: overlay.duration_seconds,
    }))
}
